/**
 * Marketing material upload — presigned S3 flow.
 *
 * Replaces the legacy `marketing-upload.php` endpoint, which streamed the whole
 * file through the web server (problematic for large videos). The browser now
 * requests a presigned PUT URL from raft (`marketing.uploadPresign`), uploads the
 * file straight to S3, then records the metadata row via `marketing.uploadComplete`.
 *
 * Wired to the "Upload" (#marketingupload) button in the marketing modal.
 */

import { api } from "../api";
import { showLoadingOverlay, hideLoadingOverlay, toastError } from "../ui/messages";
import { getDomElement, setText } from "../utils/dom";
import { putWithProgress } from "./uploadShared";
import Dropzone from "dropzone";

const ALLOWED_EXT = [
  "mp4",
  "avi",
  "mpeg",
  "mpg",
  "mov",
  "jpg",
  "jpeg",
  "png",
  "tif",
  "tiff",
  "pdf",
];

/**
 * Full presign → PUT → complete pipeline for one marketing file.
 */
async function handleMarketingFile(file: File, onSuccess?: () => void): Promise<void> {
  const ext = (file.name.split(".").pop() ?? "").toLowerCase();
  if (!ALLOWED_EXT.includes(ext)) {
    toastError(file.name + ": only jpg, png, tif, pdf or video is allowed");
    return;
  }

  if (getDomElement("marketing-nofiles") !== null) {
    getDomElement("marketing-nofiles")?.remove();
  }

  // Request a presigned PUT URL.
  showLoadingOverlay("Preparing upload...");
  let key: string;
  let url: string;
  try {
    const data = await api.marketing.uploadPresign({ filename: file.name });
    key = data.key;
    url = data.url;
  } catch (err) {
    hideLoadingOverlay();
    toastError((err as Error).message || "Could not prepare upload");
    return;
  }

  // PUT the file straight to S3.
  showLoadingOverlay('Uploading file... <span id="marketing-progress">0%</span>');
  try {
    await putWithProgress(url, file, (pct) => {
      if (getDomElement("marketing-progress") !== null) {
        setText("marketing-progress", String(pct) + "%");
      }
    });
  } catch {
    hideLoadingOverlay();
    toastError("Upload failed");
    return;
  }

  // Record the metadata row.
  showLoadingOverlay("Saving...");
  try {
    await api.marketing.uploadComplete({ key, filename: file.name, filesize: file.size });
  } catch (err) {
    hideLoadingOverlay();
    toastError((err as Error).message || "Could not save file");
    return;
  }

  hideLoadingOverlay();
  if (onSuccess) onSuccess();
}

/**
 * Attach the presigned-upload Dropzone to the #marketingupload button. Dropzone's
 * own uploader is disabled (`autoProcessQueue: false`); the whole pipeline runs in
 * `addedfile`.
 */
export function initMarketingUpload(onSuccess?: () => void, elementId = "marketingupload"): void {
  // ⚠️ The id is a parameter because TWO modals carry an upload button now: the manager
  // (`marketingupload`) and the prompt (`marketingpromptupload`). Sharing one id would
  // make `getDomElement` return whichever happened to be in the document first, and both
  // are plain string lookups that `tsc` cannot check — see `pnpm check:dom-ids`.
  const el = getDomElement(elementId);
  if (!el) return;

  // The caller's effect re-runs whenever the file list reloads — including after every
  // successful upload, since that IS the reload. Dropzone throws when a second instance is
  // attached to the same element, so drop any previous one first. `forElement` throws when
  // there is none, which is the normal first-attach path.
  try {
    Dropzone.forElement(el).destroy();
  } catch {
    // No Dropzone attached yet.
  }

  new Dropzone(el, {
    url: "#",
    autoProcessQueue: false,
    maxFilesize: 300,
    createImageThumbnails: false,
    previewTemplate: "",
    addedfile: (file: DropzoneFile) => {
      void handleMarketingFile(file, onSuccess);
    },
    accept: (_file: DropzoneFile, done: (error?: string) => void) => {
      done();
    },
  });
}
